import { isDevelopment } from 'vet-bones/utils';

import {
  createDescribeRequestMessage,
  createDescribeResponseMessage,
  createExistingModelMessage,
  createTurtleRequestMessage,
  createTurtleResponseMessage,
} from 'src/utils/voicebox/messages';
import { parseTurtleTriples } from 'src/utils/voicebox/parsing/parseTurtleTriples';
import { createExistingModelPrompt } from 'src/utils/voicebox/prompts/existingModel';
import { INITIAL_WAIT_MESSAGE } from 'src/utils/voicebox/prompts/initialMessages';
import { MODEL_PROMPT_RESPONSE } from 'src/utils/voicebox/prompts/mocks';
import { createNewModelPrompt } from 'src/utils/voicebox/prompts/newModel';
import {
  REREQUEST_FOR_PREFIXES_MESSAGE,
  REREQUEST_FOR_SYNTAX_MESSAGE,
} from 'src/utils/voicebox/prompts/rerequestMessages';
import {
  requestStreamedVoicebox,
  requestStreamedVoiceboxMocked,
} from 'src/utils/voicebox/request/requestStreamedVoicebox';
import {
  VoiceboxMessage,
  VoiceboxWrapperRequest,
  VoiceboxWrapperResponse,
  isDescribeRequestMessage,
  isDescribeResponseMessage,
  isProgressMessage,
  isTurtleRequestMessage,
  isTurtleResponseMessage,
} from 'src/utils/voicebox/types';

const isDev = isDevelopment();
// 1 initial try, 3 attempts to "fix"
const MAX_TURTLE_FIX_ATTEMPTS = 3;

export const wrappedVoiceboxRequest = async (
  { query, existingModel, conversationId, describe }: VoiceboxWrapperRequest,
  {
    defaultNamespace = 'urn:skynet:',
    openAiKey,
    onUpdateMessages,
    initialMessages,
  }: {
    defaultNamespace?: string;
    openAiKey: string;
    onUpdateMessages: (messages: VoiceboxMessage[]) => void;
    initialMessages: VoiceboxMessage[];
  }
): Promise<VoiceboxWrapperResponse> => {
  const messages: VoiceboxMessage[] = [...initialMessages];
  const isFirstMessage = !messages.some((message) => message.role === 'user');

  let requestMessage: VoiceboxMessage;
  if (isFirstMessage) {
    if (existingModel) {
      requestMessage = createTurtleRequestMessage({
        userPrompt: query,
        content: createExistingModelPrompt({
          userPrompt: query,
        }),
      });
      messages.push(createExistingModelMessage({ modelSchema: existingModel }));
    } else {
      requestMessage = createTurtleRequestMessage({
        userPrompt: query,
        content: createNewModelPrompt({
          defaultNamespace,
          userPrompt: query,
        }),
      });
    }
  } else {
    requestMessage = createTurtleRequestMessage({ userPrompt: query });
  }
  messages.push(requestMessage);

  if (isFirstMessage) {
    messages.push(INITIAL_WAIT_MESSAGE);
  }

  const mockData = query === 'a';
  const mockTurtleError = query === 'b';
  const mockDescribeError = query === 'c';
  const mockInvalidTurtle = query === 'd';
  const mock =
    isDev &&
    (mockData || mockTurtleError || mockDescribeError || mockInvalidTurtle);

  onUpdateMessages(messages);

  const errors: string[] = [];
  const { content, error } = mock
    ? await requestStreamedVoiceboxMocked({
        mockedResponse: mockInvalidTurtle
          ? '```\n:weird :turtle :that :isnt :valid\n```'
          : MODEL_PROMPT_RESPONSE,
        mockError: mockTurtleError,
      })
    : await requestStreamedVoicebox({
        openAiKey,
        messages: getFilteredMessages({ messages, requestMessage }),
      });

  const firstResponseMessage = createTurtleResponseMessage({
    content: content || error || '',
    requestMessageId: requestMessage.id,
  });
  let responseMessage = firstResponseMessage;
  messages.push(responseMessage);

  if (error) {
    responseMessage.errored = true;
  }

  let cstParseResults = parseTurtleTriples(responseMessage.code);
  if (!mock && content) {
    let numFixAttempts = 0;
    let isInvalidSyntax = Boolean(cstParseResults.errors.length);
    let isInvalidPrefixes = Boolean(cstParseResults.semanticErrors.length);

    let errorEncounteredWhileFixingTurtle: string | undefined;

    while (
      !errorEncounteredWhileFixingTurtle &&
      (isInvalidSyntax || isInvalidPrefixes) &&
      numFixAttempts < MAX_TURTLE_FIX_ATTEMPTS
    ) {
      numFixAttempts += 1;
      // prioritize general errors, then prefix errors
      const rerequestMessage = isInvalidSyntax
        ? REREQUEST_FOR_SYNTAX_MESSAGE
        : REREQUEST_FOR_PREFIXES_MESSAGE;
      messages.push(rerequestMessage);
      const { content: newContent, error: newError } =
        // eslint-disable-next-line no-await-in-loop
        await requestStreamedVoicebox({
          openAiKey,
          messages,
        });

      responseMessage = createTurtleResponseMessage({
        content: newContent || newError || '',
        requestMessageId: requestMessage.id,
        hidden: true,
      });
      messages.push(responseMessage);

      if (newContent) {
        cstParseResults = parseTurtleTriples(responseMessage.code);
        isInvalidSyntax = Boolean(cstParseResults.errors.length);
        isInvalidPrefixes = Boolean(cstParseResults.semanticErrors.length);
      }

      if (newError) {
        responseMessage.errored = true;
        errorEncounteredWhileFixingTurtle = newError;
      }
    }
  }

  if (responseMessage.errored) {
    errors.push(responseMessage.content);
  } else if (responseMessage.code) {
    // unhide last response
    responseMessage.hidden = undefined;
    // set the surrounding content of the last response to the first response
    // to aplogies, descriptions of intermittent failures, etc.
    responseMessage.before = firstResponseMessage.before;
    responseMessage.after = firstResponseMessage.after;
  }

  let describeContent: string | undefined;
  if (!responseMessage.errored && describe) {
    const describeRequestMessage = createDescribeRequestMessage({
      code: responseMessage.code,
      turtleResponseMessageId: responseMessage.id,
    });
    messages.push(describeRequestMessage);

    const { content: describeContent, error: describeError } = mock
      ? await requestStreamedVoiceboxMocked({
          mockedResponse: 'This is a description for the turtle code!',
          mockError: mockDescribeError,
        })
      : await requestStreamedVoicebox({
          openAiKey,
          messages: getFilteredMessages({
            messages,
            requestMessage: describeRequestMessage,
          }),
        });

    const describeResponseMessage = createDescribeResponseMessage({
      content: describeContent || describeError || '',
      requestMessageId: describeRequestMessage.id,
    });
    responseMessage.describeResponseMessageId = describeResponseMessage.id;
    if (describeError) {
      describeResponseMessage.errored = true;
      errors.push(describeError);
    }
    messages.push(describeResponseMessage);
  }

  onUpdateMessages(messages);

  return {
    content: content || '',
    description: describeContent,
    errors: errors.length ? errors : undefined,
    conversationId,
  };
};

const getFilteredMessages = ({
  messages,
  requestMessage,
}: {
  messages: VoiceboxMessage[];
  requestMessage: VoiceboxMessage;
}) => [
  ...messages.filter(
    (message) =>
      // never send progress messages
      !isProgressMessage(message) &&
      // never send error or pending messages
      !(isTurtleRequestMessage(message) && message.errored) &&
      !(isTurtleResponseMessage(message) && message.errored) &&
      !(isDescribeRequestMessage(message) && message.errored) &&
      !(isDescribeResponseMessage(message) && message.errored) &&
      // never send old description requests or responses
      !isDescribeRequestMessage(message) &&
      !isDescribeResponseMessage(message) &&
      // filter out here and append below so we don't get duplicates
      message.id !== requestMessage.id
  ),
  requestMessage,
  // TODO when turtle, maybe don't send old turtles?
  // TODO when describe, maybe don't send old describes?
];
